import { defaultAppUserId, supabase } from '../config/supabase';
import {
  isPlayedLineResult,
  legacyLineResult,
  scoreLineResult,
  type LineResultValue,
  type TeamResultValue,
} from './resultScoring';

export {
  isPlayedLineResult,
  legacyLineResult,
  scoreLineResult,
  type LineResultValue,
  type TeamResultValue,
} from './resultScoring';

type ScoreSummary = {
  ourTeamScore: number;
  opponentTeamScore: number;
  teamResult: TeamResultValue;
  legacyTeamResult: 'win' | 'loss' | 'tie';
};

type LineupEntryForParticipation = {
  match_event_id: string;
  league_configuration_position_id: string;
  player_id: string;
};

type LineResultForParticipation = {
  league_configuration_position_id: string;
  result_value: LineResultValue;
};

type ExistingParticipationEvent = {
  player_id: string;
  league_configuration_position_id: string | null;
  participation_event_type: string;
  allocation_count_indicator: boolean;
  manual_allocation_override_indicator: boolean;
};

export function calculateScoreSummary(results: LineResultValue[]): ScoreSummary {
  const ourTeamScore = results.filter((result) => scoreLineResult(result) === 1).length;
  const opponentTeamScore = results.filter((result) => scoreLineResult(result) === -1).length;
  const enteredCount = results.filter((result) => result !== 'Not Entered').length;
  const teamResult: TeamResultValue = enteredCount === 0
    ? 'Unknown'
    : ourTeamScore > opponentTeamScore
      ? 'Win'
      : ourTeamScore < opponentTeamScore
        ? 'Loss'
        : 'Tie';
  return {
    ourTeamScore,
    opponentTeamScore,
    teamResult,
    legacyTeamResult: teamResult === 'Win' ? 'win' : teamResult === 'Loss' ? 'loss' : 'tie',
  };
}

export async function syncPlayedParticipationForCompletedMatch(matchEventId: string, actorId = defaultAppUserId): Promise<number> {
  const [entriesResult, resultsResult, eventsResult] = await Promise.all([
    supabase
      .from('lineup_entries')
      .select('match_event_id, league_configuration_position_id, player_id')
      .eq('match_event_id', matchEventId)
      .is('deleted_at', null),
    supabase
      .from('line_results')
      .select('league_configuration_position_id, result_value')
      .eq('match_event_id', matchEventId)
      .is('deleted_at', null),
    supabase
      .from('player_participation_events')
      .select('player_id, league_configuration_position_id, participation_event_type, allocation_count_indicator, manual_allocation_override_indicator')
      .eq('match_event_id', matchEventId)
      .is('deleted_at', null),
  ]);

  if (entriesResult.error || resultsResult.error || eventsResult.error) {
    throw new Error(entriesResult.error?.message || resultsResult.error?.message || eventsResult.error?.message || 'Unable to sync participation events.');
  }

  const resultByPosition = new Map(((resultsResult.data || []) as unknown as LineResultForParticipation[]).map((result) => [result.league_configuration_position_id, result.result_value]));
  const existingEvents = (eventsResult.data || []) as unknown as ExistingParticipationEvent[];
  const existingPlayedKeys = new Set(
    existingEvents
      .filter((event) => event.participation_event_type === 'played')
      .map((event) => `${event.player_id}:${event.league_configuration_position_id || ''}`),
  );
  const scheduledByKey = new Map(existingEvents
    .filter((event) => event.participation_event_type === 'scheduled')
    .map((event) => [`${event.player_id}:${event.league_configuration_position_id || ''}`, event]));
  const lineupEntries = (entriesResult.data || []) as unknown as LineupEntryForParticipation[];
  const rows = lineupEntries
    .filter((entry) => isPlayedLineResult(resultByPosition.get(entry.league_configuration_position_id) || 'Not Entered'))
    .filter((entry) => !existingPlayedKeys.has(`${entry.player_id}:${entry.league_configuration_position_id}`))
    .map((entry) => {
      const scheduled = scheduledByKey.get(`${entry.player_id}:${entry.league_configuration_position_id}`);
      return {
      match_event_id: entry.match_event_id,
      player_id: entry.player_id,
      league_configuration_position_id: entry.league_configuration_position_id,
      participation_event_type: 'played',
      allocation_count_indicator: scheduled?.allocation_count_indicator ?? true,
      manual_allocation_override_indicator: scheduled?.manual_allocation_override_indicator ?? false,
      participation_event_reason: 'Match completed',
      participation_event_notes: 'Played event created from Match Results completion workflow.',
      created_by: actorId,
      updated_by: actorId,
      };
    });

  if (rows.length === 0) return 0;
  const { error } = await supabase.from('player_participation_events').insert(rows);
  if (error) throw new Error(error.message);
  return rows.length;
}
